import React, { useEffect, useState } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import { Package, AlertTriangle, Truck, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

export default function Inventory() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/inventory"); setD(data); })(); }, []);
  if (!d) return <div className="text-slate-400 text-sm">Loading…</div>;

  const vendors = [...new Set(d.skus.map((s) => s.vendor).filter(Boolean))].sort();
  const categories = [...new Set(d.skus.map((s) => s.category).filter(Boolean))].sort();
  const tiers = [...new Set(d.skus.map((s) => s.tier).filter(Boolean))];

  const skuCols = [
    { key: "name", label: "Reward", filter: "text", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "category", label: "Category", filter: "select", options: categories, render: (r) => <span className="capitalize">{r.category}</span> },
    { key: "tier", label: "Tier", filter: "select", options: tiers, render: (r) => <Tag tone={r.tier === "bonus" ? "amber" : "slate"}>{r.tier}</Tag> },
    { key: "vendor", label: "Vendor", filter: "select", options: vendors },
    { key: "points", label: "Points", filter: "range", align: "right", render: (r) => fmt(r.points) },
    { key: "total", label: "Total", accessor: (r) => r.inventory?.total, align: "right", render: (r) => <span className="numerals">{r.inventory?.total ?? r.stock}</span> },
    { key: "available", label: "Available", accessor: (r) => r.inventory?.available, align: "right",
      render: (r) => <Tag tone={r.inventory?.low_stock_alert ? "rose" : "emerald"}>{r.inventory?.available ?? 0}</Tag> },
    { key: "reserved", label: "Reserved", accessor: (r) => r.inventory?.reserved, align: "right",
      render: (r) => <span className="numerals text-amber-700">{r.inventory?.reserved ?? 0}</span> },
    { key: "dispatched", label: "Dispatched", accessor: (r) => r.inventory?.dispatched, align: "right",
      render: (r) => <span className="numerals text-teal-700">{r.inventory?.dispatched ?? 0}</span> },
    { key: "delivered", label: "Delivered", accessor: (r) => r.inventory?.delivered, align: "right",
      render: (r) => <span className="numerals">{r.inventory?.delivered ?? 0}</span> },
    { key: "low_stock_threshold", label: "Low-stock Alert", accessor: (r) => r.inventory?.low_stock_threshold, align: "right",
      render: (r) => <span className="text-[11px] text-slate-500 numerals">≤ {r.inventory?.low_stock_threshold ?? 10}</span> },
  ];

  const redemptionStatuses = [...new Set(d.recent_redemptions.map((r) => r.status))];
  const trackStatuses = [...new Set(d.recent_redemptions.map((r) => r.tracking_status))];

  const redCols = [
    { key: "reward_name", label: "Reward", filter: "text", render: (r) => <span className="font-medium">{r.reward_name}</span> },
    { key: "points", label: "Points", filter: "range", align: "right", render: (r) => fmt(r.points) },
    { key: "status", label: "Status", filter: "select", options: redemptionStatuses, render: (r) => <Tag tone={r.status === "delivered" ? "emerald" : r.status === "shipped" || r.status === "vendor_dispatch" ? "teal" : r.status === "released" || r.status === "exception" || r.status === "rto" ? "rose" : "amber"}>{r.status}</Tag> },
    { key: "tracking_status", label: "Tracking", filter: "select", options: trackStatuses },
    { key: "at", label: "Date", render: (r) => new Date(r.at).toLocaleDateString() },
  ];

  const totals = d.totals || {};

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Operations"
        title="Inventory & Fulfillment"
        subtitle="Live inventory states — total, available, reserved (blocked at selection), dispatched, delivered — with low-stock alerts for timely procurement decisions."
        testId="inventory-page"
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><div className="small-caps text-slate-500">Active SKUs</div><div className="serif-display text-3xl numerals mt-1">{d.skus.length}</div></Card>
        <Card><div className="small-caps text-slate-500 flex items-center gap-1"><Package size={11} /> Total stock</div><div className="serif-display text-3xl numerals mt-1">{fmt(totals.total)}</div></Card>
        <Card><div className="small-caps text-emerald-700 flex items-center gap-1"><CheckCircle2 size={11} /> Available</div><div className="serif-display text-3xl numerals mt-1 text-emerald-700">{fmt(totals.available)}</div></Card>
        <Card><div className="small-caps text-amber-700">Reserved</div><div className="serif-display text-3xl numerals mt-1 text-amber-700">{fmt(totals.reserved)}</div></Card>
        <Card><div className="small-caps text-teal-700 flex items-center gap-1"><Truck size={11} /> Dispatched</div><div className="serif-display text-3xl numerals mt-1 text-teal-700">{fmt(totals.dispatched)}</div></Card>
        <Card><div className="small-caps text-rose-700 flex items-center gap-1"><AlertTriangle size={11} /> Low-stock</div><div className="serif-display text-3xl numerals mt-1 text-rose-700">{d.low_stock.length}</div></Card>
      </div>

      <Card title="SKU inventory" subtitle="Stock states reduce/reserve immediately on physio selection. Re-procure rewards flagged 'low' before they hit zero.">
        <DataTable columns={skuCols} data={d.skus} rowKey="id" searchableKeys={["name", "vendor"]} exportFilename="cbc-inventory" testIdPrefix="skus" initialSort={{ key: "available", dir: "asc" }} />
      </Card>

      <Card title="Recent dispatches">
        <DataTable columns={redCols} data={d.recent_redemptions} rowKey="id" searchableKeys={["reward_name", "tracking_status"]} exportFilename="cbc-redemptions" testIdPrefix="redemptions" initialSort={{ key: "at", dir: "desc" }} />
      </Card>
    </div>
  );
}
